import type { FeedEmailPreference } from '@parcel/types';
import { toast } from '@parcel/ui';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiFetch } from './api.js';

export interface NotificationPreferences {
  feed_email: FeedEmailPreference;
  /** Whether the plan gets the full digest; the Free plan's email shows counts only. */
  email_allowed: boolean;
}

export const notificationPreferenceKeys = {
  member: (workspaceId: string | undefined) => ['notification-preferences', workspaceId] as const,
};

const path = (workspaceId: string) =>
  `/me/notification-preferences?${new URLSearchParams({ workspace_id: workspaceId })}`;

export function getNotificationPreferences(workspaceId: string) {
  return apiFetch<NotificationPreferences>(path(workspaceId));
}

export function putNotificationPreferences(workspaceId: string, feedEmail: FeedEmailPreference) {
  return apiFetch<NotificationPreferences>(path(workspaceId), {
    method: 'PUT',
    body: JSON.stringify({ feed_email: feedEmail }),
  });
}

export function useNotificationPreferences(workspaceId: string | undefined) {
  return useQuery({
    queryKey: notificationPreferenceKeys.member(workspaceId),
    queryFn: () => getNotificationPreferences(workspaceId as string),
    enabled: Boolean(workspaceId),
  });
}

export function useNotificationPreferenceMutation(workspaceId: string | undefined) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (feedEmail: FeedEmailPreference) =>
      putNotificationPreferences(workspaceId as string, feedEmail),
    onSuccess: (data) => {
      client.setQueryData(notificationPreferenceKeys.member(workspaceId), data);
      toast.success('Saved');
    },
    onError: () => toast.error("Couldn't save your notification settings."),
  });
}
